import React from 'react'
import { ChevronLeft } from 'lucide-react'
import { Button } from '@klear/ui/components/button'

export type WizardStep = {
  id: string
  label: string
  depth?: number
  section?: string
}

export type WizardStepNode = WizardStep & { depth: number }

export interface WizardProps {
  name: string
  subtitle?: string
  steps: WizardStep[]
  activeStepId: string
  onStepSelect?: (stepId: string) => void
  isStepDisabled?: (stepId: string) => boolean
  onBack: () => void
  backLabel?: string
  headerActions?: React.ReactNode
  summary?: React.ReactNode
  toolbar?: React.ReactNode
  error?: React.ReactNode
  renderStep?: (step: WizardStepNode, state: { active: boolean; disabled: boolean; index: number }) => React.ReactNode
  contentMaxWidthClass?: string
  children: React.ReactNode
}

export function Wizard({
  name,
  subtitle,
  steps,
  activeStepId,
  onStepSelect,
  isStepDisabled,
  onBack,
  backLabel = 'Back',
  headerActions,
  summary,
  toolbar,
  error,
  renderStep,
  contentMaxWidthClass = 'max-w-[600px]',
  children
}: WizardProps) {
  const sidebarSteps: WizardStepNode[] = steps.map((s) => ({ ...s, depth: s.depth ?? 0 }))

  return (
    <div className="flex h-full min-h-0 bg-muted/30 overflow-hidden text-sm">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-card px-6 py-3">
          <Button
            type="button"
            variant="link"
            className="text-muted-foreground hover:text-foreground -ml-2 h-auto p-0"
            onClick={onBack}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            {backLabel}
          </Button>
          {headerActions ? <div className="flex items-center gap-2">{headerActions}</div> : null}
        </div>
        {summary ? <div className="shrink-0 border-b border-border bg-card">{summary}</div> : null}
        {toolbar ? <div className="shrink-0">{toolbar}</div> : null}
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <div className={`mx-auto w-full space-y-4 ${contentMaxWidthClass}`}>
            {error}
            {children}
          </div>
        </div>
      </div>
      <aside className="w-56 shrink-0 border-l border-border bg-card flex flex-col">
        <div className="p-4 border-b border-border">
          <h2 className="text-sm font-semibold">{name}</h2>
          {subtitle ? <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p> : null}
        </div>
        <nav className="space-y-0.5 p-3">
          {sidebarSteps.map((step, index) => {
            const isChild = step.depth > 0
            const isActive = step.id === activeStepId

            let disabled: boolean
            if (isStepDisabled != null) {
              disabled = isStepDisabled(step.id)
            } else {
              disabled = onStepSelect == null && step.id !== activeStepId
            }

            const topLevelNumber = isChild ? 0 : sidebarSteps.slice(0, index + 1).filter((s) => s.depth === 0).length

            const prevSection = index > 0 ? sidebarSteps[index - 1]?.section : undefined
            const showSection = step.section != null && step.section !== prevSection

            const defaultRow = (
              <>
                {!isChild && step.section == null ? <span className="mr-2">{topLevelNumber}.</span> : null}
                {step.label}
              </>
            )

            return (
              <div key={step.id}>
                {showSection ? (
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-2 mb-1 mt-3 first:mt-0">
                    {step.section}
                  </div>
                ) : null}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (!disabled) onStepSelect?.(step.id)
                  }}
                  style={isChild ? { paddingLeft: `${0.75 + step.depth * 0.875}rem` } : undefined}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                    isChild ? 'border-l border-border' : ''
                  } ${
                    isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {renderStep ? renderStep(step, { active: isActive, disabled, index }) : defaultRow}
                </button>
              </div>
            )
          })}
        </nav>
      </aside>
    </div>
  )
}
